import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, useScroll } from "framer-motion";
import { Loader2, Headphones } from "lucide-react";
import { api } from "@/lib/api";
import { ESSAIS_UI, pickLang, ROMAN } from "@/lib/essaisText";
import { EssaiHeader } from "@/components/EssaiHeader";
import { EssaiShare } from "@/components/EssaiShare";
import { EssaiAudio } from "@/components/EssaiAudio";
import { Footer } from "@/components/Footer";

const rise = { initial: { opacity: 0, y: 20 }, whileInView: { opacity: 1, y: 0 }, viewport: { once: true, amount: 0.3 }, transition: { duration: 0.8 } };
const P = ({ children }) => <motion.p {...rise} className="text-foreground/80 text-base sm:text-lg leading-[1.85] mb-6">{children}</motion.p>;
const H = ({ children, id }) => <motion.h2 {...rise} id={id} className="font-display text-2xl sm:text-3xl text-primary mt-16 mb-6 leading-snug">{children}</motion.h2>;

const Part = ({ p, i }) => (
  <section data-testid={`essai-part-${i}`}>
    <H>{ROMAN[i]}. {p.title}</H>
    {p.lead && <P>{p.lead}</P>}
    {(p.points || []).map((pt, k) => (
      <motion.div key={k} {...rise} className="mb-6 border-l-2 border-primary/40 pl-5">
        <p className="text-foreground/80 text-base sm:text-lg leading-[1.85]"><strong className="text-foreground font-semibold">{pt.label} :</strong> {pt.text}</p>
      </motion.div>
    ))}
    {p.close && <P>{p.close}</P>}
  </section>
);

export default function Essai() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const lang = pickLang(i18n.language);
  const U = ESSAIS_UI[lang];
  const [doc, setDoc] = useState(null);
  const [err, setErr] = useState(false);
  const { scrollYProgress } = useScroll();

  useEffect(() => { setErr(false); api.get(`/essais/${slug}`, { params: { lang } }).then(({ data }) => setDoc(data)).catch(() => setErr(true)); window.scrollTo(0, 0); }, [slug, lang]);
  useEffect(() => { if (doc) document.title = `${doc.title} — Cocoon Mind`; }, [doc]);

  const words = doc ? JSON.stringify([doc.intro, doc.parts, doc.conclusion]).split(/\s+/).length : 0;

  return (
    <div className="min-h-screen bg-background text-foreground flex flex-col">
      <motion.div style={{ scaleX: scrollYProgress }} className="fixed top-0 left-0 right-0 h-0.5 bg-primary origin-left z-[60]" />
      <EssaiHeader to="/essais" label={U.back} />
      {err && <p className="pt-40 text-center text-foreground/60" data-testid="essai-error">{U.notFound} <Link to="/essais" className="text-primary hover:underline">{U.all} →</Link></p>}
      {!doc && !err && <Loader2 className="mx-auto mt-40 animate-spin text-primary" />}
      {doc && (
        <>
          <section className="relative h-[70vh] min-h-[480px] overflow-hidden">
            <motion.img src={`/derive/covers/${slug}.jpg`} alt="" initial={{ scale: 1.1 }} animate={{ scale: 1 }} transition={{ duration: 5 }} className="absolute inset-0 h-full w-full object-cover opacity-45 blur-[6px]" />
            <div className="absolute inset-0 bg-gradient-to-b from-background/60 via-background/70 to-background" />
            <div className="relative h-full max-w-3xl mx-auto px-6 flex flex-col justify-end pb-14">
              <p className="text-[0.7rem] uppercase tracking-[0.28em] text-primary mb-4" data-testid="essai-series">{U.kicker} · {U.series} · N°{doc.num}</p>
              <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.05]" data-testid="essai-title">{doc.title}</h1>
              <p className="mt-4 font-display italic text-lg md:text-xl text-foreground/75" data-testid="essai-subtitle">{doc.subtitle}</p>
              <p className="mt-6 text-sm text-foreground/55">{U.author} · {Math.max(1, Math.round(words / 230))} {U.minutes}</p>
            </div>
          </section>
          <main className="max-w-2xl w-full mx-auto px-6 pb-20" data-testid="essai-article">
            <div className="mb-12 flex flex-wrap items-center gap-3">
              <Link to="/radio" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:-translate-y-0.5 transition-transform" data-testid="essai-listen-btn"><Headphones size={16} /> {U.listen}</Link>
            </div>
            {doc.audio_url && <div className="mb-12"><EssaiAudio src={doc.audio_url} seconds={doc.audio_seconds} label={U.audio} /></div>}
            <H>{doc.intro_title}</H>
            {(doc.intro || []).map((t, k) => <P key={k}>{t}</P>)}
            {(doc.parts || []).map((p, i) => <Part key={i} p={p} i={i} />)}
            <H>{doc.conclusion_title}</H>
            {(doc.conclusion || []).map((t, k) => <P key={k}>{t}</P>)}
            <p className="mt-14 pt-8 border-t border-primary/20 font-display italic text-foreground/60" data-testid="essai-publisher">{U.publisher}</p>
            <div className="mt-12 rounded-3xl border border-primary/25 bg-card/40 p-6 sm:p-8">
              <p className="font-display text-2xl mb-5">{U.share}</p>
              <EssaiShare slug={slug} lang={lang} title={doc.title} excerpt={doc.excerpt} U={U} />
            </div>
            <Link to="/essais" className="mt-10 inline-block text-sm text-primary hover:underline" data-testid="essai-all-link">{U.all} →</Link>
          </main>
        </>
      )}
      <Footer />
    </div>
  );
}
